<template>
  <EmailNotificationsTabSkeleton v-if="showSkeleton" variant="templates" />
  <div
    v-else
    class="automations-scroll-panel automations-scroll-panel--stack"
    data-testid="email-notifications__templates"
  >
    <div class="fc-notification-toolbar">
      <SearchBar
        v-model="search"
        placeholder="Search templates"
        width="17.5rem"
        data-testid-prefix="email-notifications__templates-search"
        class="fc-notification-toolbar__search"
      />
      <v-btn
        v-if="hasActiveFilters"
        text
        class="fc-notification-toolbar__clear text-none"
        data-testid="email-notifications__templates-clear-filter"
        @click="clearAllFilters"
      >
        Clear Filter
      </v-btn>
      <span class="fc-notification-toolbar__spacer" />
      <span class="fc-notification-toolbar__tz">{{ timezoneLabel }}</span>
      <ActivityLog module-name="financialclose_notification" hide-trigger use-mdi-history />
      <NewTemplateMenu @select="$emit('create-template', $event)" />
    </div>
    <div class="fc-notification-banner">
      <v-icon small color="#1f4f80">mdi-information-outline</v-icon>
      <p>
        Templates with <b>Auto-apply</b> on are added to every new cycle. Edits here affect cycles created from
        now on. A running cycle keeps its own copy — change it in that cycle's <b>Cycle Settings › Automations</b>.
      </p>
    </div>
    <div class="automations-scroll-panel__split">
    <section class="automations-scroll-panel__pane">
    <div class="fc-notification-section-head fc-notification-section-head--first">
      <v-icon class="fc-notification-section-head__icon">mdi-alert-octagon-outline</v-icon>
      <div>
        <div class="fc-notification-section-head__title">Escalations</div>
        <div class="fc-notification-section-head__sub">Email a lead when tasks breach their due-date SLA.</div>
      </div>
    </div>
    <NotificationsTable
      ref="escalationsTable"
      class="notifications-table--escalation"
      :items="filterRows(escalationTemplates)"
      :headers="escalationHeaders"
      :row-enricher="tableFilterRow"
      :show-toolbar="false"
      @filter-active-change="escalationColumnFilterActive = $event"
      empty-text="No escalation templates yet"
      data-testid-prefix="email-notifications-escalations"
    >
      <template #cell-name="{ item }">
        <div class="fc-notification-name">
          <div class="fc-notification-name__title">{{ displayName(item) }}</div>
        </div>
      </template>
      <template #cell-subject="{ item }">
        <div class="fc-notification-name">
          <div class="fc-notification-name__title">{{ item.subject || '—' }}</div>
        </div>
      </template>
      <template #cell-recipients="{ item }">
        <div class="fc-notification-inline">
          <NotificationRecipientChips :items="(item.meta && item.meta.toUsers) || []" :visible-count="2" />
        </div>
      </template>
      <template #cell-locations="{ item }">
        <div class="fc-notification-inline">
          <OverflowList :items="locationNames(item)" :visible-count="2" />
        </div>
      </template>
      <template #cell-sendsAt="{ item }">
        <NotificationScheduleCell
          :schedule="item.meta && item.meta.schedule"
          :cron="item.schedulerTiming"
          :kind="item.kind"
        />
      </template>
      <template #cell-activeWindow="{ item }">
        {{ activeWindowLabel(item) }}
      </template>
      <template #cell-status="{ item }">
        <NotificationStatusToggle
          :value="item.flag === true || item.status === true"
          :loading="isStatusPending(item._id)"
          data-testid="email-notifications-escalations__status-toggle"
          @change="onToggle(item, $event)"
        />
      </template>
      <template #actions="{ item }">
        <NotificationActionIcon
          :tooltip="EMAIL_NOTIFICATION.ACTION_TOOLTIP.EDIT"
          :icon-src="EMAIL_NOTIFICATION.ACTION_ICON.EDIT"
          data-testid="email-notifications-escalations__edit-btn"
          @click="$emit('edit', item)"
        />
        <NotificationActionIcon
          v-if="showHistory"
          :tooltip="EMAIL_NOTIFICATION.ACTION_TOOLTIP.EMAIL_HISTORY"
          :icon-src="EMAIL_NOTIFICATION.ACTION_ICON.EMAIL"
          data-testid="email-notifications-escalations__email-history-btn"
          @click="$emit('view-activity', item)"
        />
        <ActivityLog :resource-id="item._id" module-name="financialclose_notification" />
        <NotificationDeleteAction
          :notification="item"
          :loading="isDeletePending(item._id)"
          data-testid="email-notifications-escalations__delete-btn"
          @confirm="onDelete(item)"
        />
      </template>
    </NotificationsTable>
    </section>
    <section class="automations-scroll-panel__pane">
    <div class="fc-notification-section-head">
      <v-icon class="fc-notification-section-head__icon">mdi-view-dashboard-outline</v-icon>
      <div>
        <div class="fc-notification-section-head__title">Dashboard digests</div>
        <div class="fc-notification-section-head__sub">Email a summary of the dashboard on a schedule.</div>
      </div>
    </div>
    <NotificationsTable
      ref="digestsTable"
      class="notifications-table--digest"
      :items="filterRows(digestTemplates)"
      :headers="digestHeaders"
      :row-enricher="tableFilterRow"
      :show-toolbar="false"
      @filter-active-change="digestColumnFilterActive = $event"
      empty-text="No dashboard digest templates yet"
      data-testid-prefix="email-notifications-digests"
    >
      <template #cell-name="{ item }">
        <div class="fc-notification-name">
          <div class="fc-notification-name__title">{{ displayName(item) }}</div>
        </div>
      </template>
      <template #cell-subject="{ item }">
        <div class="fc-notification-name">
          <div class="fc-notification-name__title">{{ item.subject || '—' }}</div>
        </div>
      </template>
      <template #cell-recipients="{ item }">
        <div class="fc-notification-inline">
          <NotificationRecipientChips :items="(item.meta && item.meta.toUsers) || []" :visible-count="2" />
        </div>
      </template>
      <template #cell-locations="{ item }">
        <div class="fc-notification-inline">
          <OverflowList :items="locationNames(item)" :visible-count="2" />
        </div>
      </template>
      <template #cell-sendsAt="{ item }">
        <NotificationScheduleCell
          :schedule="item.meta && item.meta.schedule"
          :cron="item.schedulerTiming"
          :kind="item.kind"
        />
      </template>
      <template #cell-activeWindow="{ item }">
        {{ activeWindowLabel(item) }}
      </template>
      <template #cell-status="{ item }">
        <NotificationStatusToggle
          :value="item.flag === true || item.status === true"
          :loading="isStatusPending(item._id)"
          data-testid="email-notifications-digests__status-toggle"
          @change="onToggle(item, $event)"
        />
      </template>
      <template #actions="{ item }">
        <NotificationActionIcon
          :tooltip="EMAIL_NOTIFICATION.ACTION_TOOLTIP.EDIT"
          :icon-src="EMAIL_NOTIFICATION.ACTION_ICON.EDIT"
          data-testid="email-notifications-digests__edit-btn"
          @click="$emit('edit', item)"
        />
        <NotificationActionIcon
          v-if="showHistory"
          :tooltip="EMAIL_NOTIFICATION.ACTION_TOOLTIP.EMAIL_HISTORY"
          :icon-src="EMAIL_NOTIFICATION.ACTION_ICON.EMAIL"
          data-testid="email-notifications-digests__email-history-btn"
          @click="$emit('view-activity', item)"
        />
        <ActivityLog :resource-id="item._id" module-name="financialclose_notification" />
        <NotificationDeleteAction
          :notification="item"
          :loading="isDeletePending(item._id)"
          data-testid="email-notifications-digests__delete-btn"
          @confirm="onDelete(item)"
        />
      </template>
    </NotificationsTable>
    </section>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import {
  digestTemplateHeaders,
  EMAIL_NOTIFICATION,
  escalationTemplateHeaders,
} from '../../../../utils/constants';
import { browserTimezoneLabel } from '../../../../utils/notificationScheduleUtils';
import {
  activeWindowLabel,
  locationIdsFromMeta,
  notificationTableFilterRow,
  storedNotificationName,
  resolveLocationNames,
} from '../../../../utils/notificationNameUtils';
import ActivityLog from '../../../../components/ActivityLog/ActivityLog.vue';
import OverflowList from '../../../../components/OverflowList/OverflowList.vue';
import SearchBar from '../../../../components/SearchBar/SearchBar.vue';
import {
  NotificationsTable,
  NotificationStatusToggle,
  NotificationDeleteAction,
  NotificationActionIcon,
  NotificationScheduleCell,
  NotificationRecipientChips,
  EmailNotificationsTabSkeleton,
} from '../../../../components/Notifications';
import NewTemplateMenu from './NewTemplateMenu.vue';

export default {
  name: 'CycleTemplatesTable',
  components: {
    EmailNotificationsTabSkeleton,
    ActivityLog,
    OverflowList,
    SearchBar,
    NotificationsTable,
    NotificationStatusToggle,
    NotificationDeleteAction,
    NotificationActionIcon,
    NotificationScheduleCell,
    NotificationRecipientChips,
    NewTemplateMenu,
  },
  props: {
    initialLoading: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    showHistory: { type: Boolean, default: true },
  },
  data() {
    return {
      search: '',
      escalationColumnFilterActive: false,
      digestColumnFilterActive: false,
      EMAIL_NOTIFICATION,
    };
  },
  computed: {
    ...mapGetters('emailNotifications', [
      'escalationTemplates',
      'digestTemplates',
      'fetchLocations',
      'isStatusPending',
      'isDeletePending',
    ]),
    escalationHeaders() {
      return escalationTemplateHeaders();
    },
    digestHeaders() {
      return digestTemplateHeaders();
    },
    timezoneLabel() {
      return browserTimezoneLabel();
    },
    hasActiveFilters() {
      return !!(this.search || '').trim()
        || this.escalationColumnFilterActive
        || this.digestColumnFilterActive;
    },
    showSkeleton() {
      if (this.initialLoading) return true;
      const escalations = this.escalationTemplates || [];
      const digests = this.digestTemplates || [];
      return this.loading && !escalations.length && !digests.length;
    },
  },
  methods: {
    ...mapActions('emailNotifications', ['changeStatusForNotification', 'deleteNotification']),
    filterRows(rows) {
      const search = (this.search || '').toLowerCase();
      if (!search) return rows || [];
      return (rows || []).filter((item) => {
        const haystack = [item.name, item.subject, item.feature]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(search);
      });
    },
    tableFilterRow(item) {
      return notificationTableFilterRow(item, { locations: this.fetchLocations });
    },
    clearAllFilters() {
      this.search = '';
      this.$refs.escalationsTable?.clearFilters();
      this.$refs.digestsTable?.clearFilters();
    },
    displayName(item) {
      return storedNotificationName(item) || '—';
    },
    locationNames(item) {
      return resolveLocationNames(locationIdsFromMeta(item.meta), this.fetchLocations);
    },
    activeWindowLabel,
    async onToggle(item, next) {
      try {
        await this.changeStatusForNotification({
          id: item._id,
          status: next ? EMAIL_NOTIFICATION.STATUS.ENABLED : EMAIL_NOTIFICATION.STATUS.DISABLED,
        });
      } catch (error) {
        console.error('[CycleTemplatesTable]:', error);
      }
    },
    async onDelete(item) {
      try {
        await this.deleteNotification({ id: item._id });
        this.$emit('deleted', item);
      } catch (error) {
        console.error('[CycleTemplatesTable]:', error);
      }
    },
  },
};
</script>
